import { useEffect, useState } from "react";
import axios from "axios";
import { getData, storeData } from "@/lib/utils";
import api from "@/store/api";
import { io } from "socket.io-client";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLogin from "./use-login";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { getMyCompany, updateAuthUser } from "@/store/authSlice";
import { useDispatch } from "react-redux";
import { routes } from "@/consts";
import { useNavigate } from "react-router-dom";

interface Notification {
  _id: string;
  userId: string;
  data: any;

  type: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const useNotification = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { refreshToken } = useLogin();
  const authUser = useSelector((state: RootState) => state.auth.authUser);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (authUser?.id) {
      const baseUrl = `https://medix-production-5f4f.up.railway.app`;
      // const token = getData("tokens")?.accessToken;
      // const baseUrl = `https://medix-oqmn.onrender.com`;
      const socketInstance = io(baseUrl, {
        query: { userId: authUser.id },
        transports: ["websocket"],
      });

      socketInstance.on("notification", (newNotification: Notification) => {
        console.log(newNotification);
        setNotifications((prev) => [newNotification, ...prev]);
        if (!newNotification.isRead) {
          setUnreadCount((prev) => prev + 1);
        }
      });

      return () => {
        socketInstance.disconnect();
      };
    }
  }, [authUser]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetchNotifications = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/notifications/getAll?pageSize=10&pageNumber=1&withPaging=false`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNotifications(response.data.data);
      setUnreadCount(
        response.data.filter((n: Notification) => !n.isRead).length
      );
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.fetchNotifications")
        );
        if (err.response?.data?.details === "Unauthorized") {
          const res = await refreshToken();
          if (res) {
            storeData("tokens", res.data.tokens);
            window.location.reload();
          } else {
            dispatch(updateAuthUser(null));
            dispatch(getMyCompany(null));
            localStorage.removeItem("tokens");
            localStorage.removeItem("userInfo");
            navigate(routes.home);
          }
        }
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const token = getData("tokens")?.accessToken;
      await api.put(
        `/notifications/mark-as-read/${id}`,
        { id: id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNotifications((prevv) =>
        prevv.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => prev - 1);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ||
            t("alerts.error.markNotificationAsRead")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        notifications
          .filter((n) => !n.isRead)
          .map((notification) => markAsRead(notification._id))
      );
      setUnreadCount(0);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ||
            t("alerts.error.markAllNotificationsAsRead")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const token = getData("tokens")?.accessToken;
      await api.delete(`/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prevt) => prevt.filter((n) => n._id !== id));
      setUnreadCount((prevd) =>
        notifications.find((n) => n._id === id && !n.isRead) ? prevd - 1 : prevd
      );
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.deleteNotification")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
    }
  };

  const deleteAllNotifications = async () => {
    try {
      const token = getData("tokens")?.accessToken;
      const userId = getData("user")?.id;
      await api.delete(`/notifications/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ||
            t("alerts.error.deleteAllNotifications")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  };
};

export default useNotification;
